import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Cookie, X } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'facefable_cookie_consent';

export default function CookieBanner() {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner immediately for GDPR compliance
      setShowBanner(true);
    } else {
      // Initialize Mixpanel if analytics consent was previously given
      const parsed = JSON.parse(consent);
      if (parsed.analytics && window.mixpanel) {
        window.mixpanel.init('412158d065323eacd77a938b122af35f', {
          autocapture: true,
          record_sessions_percent: 100,
          api_host: 'https://api-eu.mixpanel.com',
        });
      }
    }
  }, []);

  const acceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    
    // Initialize Mixpanel immediately when analytics accepted
    if (window.mixpanel && window.mixpanel.init) {
      window.mixpanel.init('412158d065323eacd77a938b122af35f', {
        autocapture: true,
        record_sessions_percent: 100,
        api_host: 'https://api-eu.mixpanel.com',
      });
    }
    
    setShowBanner(false);
  };

  const acceptNecessary = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    setShowBanner(false);
  };

  const savePreferences = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consent));
    
    // Initialize Mixpanel only if analytics accepted
    if (preferences.analytics && window.mixpanel && window.mixpanel.init) {
      window.mixpanel.init('412158d065323eacd77a938b122af35f', {
        autocapture: true,
        record_sessions_percent: 100,
        api_host: 'https://api-eu.mixpanel.com',
      });
    }
    
    setShowBanner(false);
    setShowPreferences(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-safe animate-in slide-in-from-bottom duration-300">
      <Card className="max-w-3xl mx-auto shadow-elegant border-border/50 bg-card/95 backdrop-blur-sm">
        <div className="p-6">
          {!showPreferences ? (
            // Main banner
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-primary" />
                  <h3 className="font-semibold text-body">Cookie Preferences</h3>
                </div>
                <p className="text-body-sm text-muted-foreground">
                  We use cookies to improve your experience, analyse traffic, and personalise content. 
                  You can customise your preferences or accept all cookies.{' '}
                  <a href="/cookie-policy" className="underline hover:text-foreground">
                    Read our cookie policy
                  </a>
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  onClick={() => setShowPreferences(true)}
                  className="w-full h-11"
                >
                  Customise
                </Button>
                <Button
                  variant="outline"
                  onClick={acceptNecessary}
                  className="w-full h-11"
                >
                  Necessary Only
                </Button>
                <Button
                  onClick={acceptAll}
                  className="w-full h-11 bg-primary hover:bg-primary/90"
                >
                  Accept All
                </Button>
              </div>
            </div>
          ) : (
            // Preferences panel
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-body">Customise Cookie Preferences</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowPreferences(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <div className="font-medium text-body-sm">Strictly Necessary</div>
                    <p className="text-micro text-muted-foreground mt-1">
                      Required for core functionality like authentication and security. Cannot be disabled.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-10 h-5 bg-primary rounded-full flex items-center justify-end px-0.5">
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <div className="font-medium text-body-sm">Analytics & Performance</div>
                    <p className="text-micro text-muted-foreground mt-1">
                      Help us understand how you use the app to improve your experience.
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                    className="ml-4"
                  >
                    <div
                      className={`w-10 h-5 rounded-full flex items-center transition-colors ${
                        preferences.analytics ? 'bg-primary justify-end' : 'bg-muted-foreground/30 justify-start'
                      } px-0.5`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </button>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-3 rounded-lg bg-muted/50">
                  <div className="flex-1">
                    <div className="font-medium text-body-sm">Marketing & Advertising</div>
                    <p className="text-micro text-muted-foreground mt-1">
                      Used to show you relevant ads and measure campaign effectiveness.
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                    className="ml-4"
                  >
                    <div
                      className={`w-10 h-5 rounded-full flex items-center transition-colors ${
                        preferences.marketing ? 'bg-primary justify-end' : 'bg-muted-foreground/30 justify-start'
                      } px-0.5`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full" />
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowPreferences(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={savePreferences}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}
